import type { ReactNode } from "react";
import type { Matcher } from "@daypicker/react";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import type {
    DatePickerButtonProps,
    DatePickerTriggerElement,
} from "@design-system/DatePicker/DatePicker.types";
import { Field, pickFieldControlAriaProps } from "@design-system/Field/Field";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import type { FieldPath, FieldPathEmptyValue } from "./fieldPath.types";
import { pickChromeProps } from "./kitChromeProps";
import { type KitClearableProps, toClearTarget } from "./kitClearable";
import type { KitFieldProps } from "./kitFieldProps.types";

type DateFieldValue = string | null | undefined;

/** Paths of `Values` that hold a date-only string (`yyyy-MM-dd`). */
export type DateFieldPath<Values> = FieldPath<Values, DateFieldValue, string>;

export type DateFieldProps<Values, Name extends string> = KitFieldProps<
    Name,
    DatePickerTriggerElement,
    string | FieldPathEmptyValue<Values, Name>
> &
    KitClearableProps<FieldPathEmptyValue<Values, Name>> & {
        /** Both variants use a button trigger; `panel-input` adds typing. */
        variant?: "button" | "panel-input";
        /** The picker's own label: names the popover and the `panel-input` typing field. */
        pickerLabel?: string;
        placeholder?: string;
        displayFormat?: string;
        minDate?: string;
        maxDate?: string;
        disabledDates?: Matcher | Matcher[];
        showTriggerIcon?: boolean;
        buttonProps?: DatePickerButtonProps;
        portalContainer?: HTMLElement;
    };

/**
 * Single-day date field bound to a typed Formik path. Not clearable unless
 * `clearable` comes with an `emptyValue` the path allows; an empty entry on
 * a non-clearable field keeps the last date.
 */
export const DateField = <
    Values,
    Name extends DateFieldPath<Values> = DateFieldPath<Values>,
>(
    props: DateFieldProps<Values, Name>,
): ReactNode => {
    const {
        name,
        ref,
        disabled,
        required,
        onValueChange,
        variant = "button",
        pickerLabel,
        placeholder,
        displayFormat,
        minDate,
        maxDate,
        disabledDates,
        showTriggerIcon,
        buttonProps,
        portalContainer,
    } = props;
    const clearTarget = toClearTarget(props);

    return (
        <FormikField<DateFieldValue>
            name={name}
            control="button"
            {...pickChromeProps(props)}
        >
            {({ field, helpers, invalid }) => {
                const handleValueChange = (value: string | undefined) => {
                    if (value !== undefined) {
                        void helpers.setValue(value);
                        onValueChange?.(value);
                    } else if (clearTarget) {
                        void helpers.setValue(clearTarget.emptyValue);
                        onValueChange?.(clearTarget.emptyValue);
                    }
                };

                return (
                    <Field.Control
                        render={(controlProps) => (
                            <DatePicker
                                mode="day"
                                variant={variant}
                                label={pickerLabel}
                                ref={ref}
                                name={name}
                                value={field.value ?? undefined}
                                onValueChange={handleValueChange}
                                onBlur={() => void helpers.setTouched(true)}
                                invalid={invalid}
                                disabled={disabled}
                                required={required}
                                clearable={clearTarget !== undefined}
                                placeholder={placeholder}
                                displayFormat={displayFormat}
                                minDate={minDate}
                                maxDate={maxDate}
                                disabledDates={disabledDates}
                                showTriggerIcon={showTriggerIcon}
                                buttonProps={buttonProps}
                                portalContainer={portalContainer}
                                fieldControlProps={{
                                    ...pickFieldControlAriaProps(controlProps),
                                    ...ariaLabelledbyProps(
                                        props["aria-labelledby"],
                                    ),
                                }}
                            />
                        )}
                    />
                );
            }}
        </FormikField>
    );
};
